// @ts-nocheck
// @ts-nocheck
/**
 * SimEngineV2.ts — SimForge Baseball Full Simulation Engine
 * =====================================================
 *
 * Every at-bat is a pitcher-batter matchup where ALL attributes matter.
 * League context shifts baselines so stats scale with overall quality.
 * Steep exponential curves ensure attribute hierarchy is respected.
 * Fielding is a 3-gate system (Reach → Handle → Throw).
 * Pitchers have 5 distinct skills; Batters have 8.
 *
 * No external dependencies — pure TS, works in browser and Node.js.
 */

// ============================================================
// TYPES
// ============================================================

export interface SimPlayer {
  id: string;
  first_name: string;
  last_name: string;
  position: string;
  team_id: string;
  is_hitter: boolean;
  is_starter?: boolean;
  batting_hand: string;
  throwing_hand: string;
  // Hitter attributes
  contact: number;
  battingAttr: number;  // renamed from "batting_average" to avoid confusion
  power: number;
  discipline: number;
  speed: number;
  range: number;
  glove: number;
  arm: number;
  // Pitcher attributes
  velocity: number;
  movement: number;
  control: number;
  deception: number;
  hold_runner: number;
  // Hidden attributes (both)
  clutch: number;
  consistency: number;
  durability: number;
  intelligence: number;
  // State
  current_stamina: number;
  last_game_day: number;
}

export interface TeamStrategy {
  // Offense
  stealAttempts: number;         // 0-100: how aggressively to run
  sacrificeBunts: number;        // 0-100: how often to bunt runners over
  hittingApproach: number;       // 0-100: small ball (contact) vs swing for power
  baseRunningAggression: number; // 0-100: take extra bases, score from 2nd on singles
  intentionalWalks: number;      // 0-100: how often to put dangerous hitters on intentionally

  // Pitching / Defense
  pitcherManagement: number;     // 0-100: aggressive hook (pull early) vs ride starters deep
  shiftFrequency: number;        // 0-100: how often to shift vs pull hitters (reduces hits on grounders)

  // New sliders
  bullpenUsage: number;          // 0-100: use high-leverage relievers early vs save them for 9th
  defenseFirst: number;          // 0-100: prioritize fielding over bat in lineup construction
  lineupProtection: number;      // 0-100: bat best hitters together (3-4-5) vs spread across lineup
}

export interface DotPortrait {
  bgColor?: string;
  eyeStyle?: string;
  eyeColor?: string;
  eyeSize?: number;
  mouthStyle?: string;
  mouthColor?: string;
  hairStyle?: string;
  hairColor?: string;
  facialHairStyle?: string;
  facialHairColor?: string;
}

export interface BatterLine {
  playerId: string; playerName: string; position: string; teamId: string;
  plateAppearances: number; atBats: number; runs: number; hits: number;
  doubles: number; triples: number; homeRuns: number; rbi: number;
  walks: number; strikeouts: number; stolenBases: number; caughtStealing: number;
  hitByPitch: number; sacrificeFlies: number; sacrificeHits: number; groundedIntoDP: number;
}

export interface PitcherLine {
  playerId: string; playerName: string; teamId: string;
  inningsPitched: number; battersFaced: number; pitchesThrown: number;
  hitsAllowed: number; runsAllowed: number; earnedRuns: number;
  walksAllowed: number; strikeoutsPitched: number; homeRunsAllowed: number;
  win: boolean; loss: boolean; save: boolean; outsRecorded: number; isStarter: boolean;
}

export interface FielderLine {
  playerId: string; playerName: string; position: string; teamId: string;
  putouts: number; assists: number; errors: number; doublePlays: number;
}

export interface PlayByPlayAtBat {
  batter: { id: string; name: string; position: string };
  pitcher: { id: string; name: string };
  pitch: { velocity: number; movement: number; inZone: boolean };
  decision: 'swing' | 'take';
  result: {
    type: string; exitVelocity?: number; launchAngle?: number;
    sprayDirection?: number; distance?: number; fielder?: string;
  };
  runsScored: number;
  baseRunnersAfter: { first: string | null; second: string | null; third: string | null };
  outsAfter: number;
}

export interface GameResult {
  homeScore: number; awayScore: number;
  homeHits: number; awayHits: number;
  homeErrors: number; awayErrors: number;
  innings: number;
  lineScore: { inning: number; homeRuns: number; awayRuns: number }[];
  homeBatters: BatterLine[];
  awayBatters: BatterLine[];
  homePitchers: PitcherLine[];
  awayPitchers: PitcherLine[];
  homeFielding: FielderLine[];
  awayFielding: FielderLine[];
  winningPitcherId: string | null;
  losingPitcherId: string | null;
  savePitcherId: string | null;
  atBats: PlayByPlayAtBat[];
}

// ============================================================
// UTILITIES
// ============================================================

const clamp = (v: number, lo = 20, hi = 99) => Math.max(lo, Math.min(hi, v));

/** Sigmoid: smooth S-curve around midpoint */
function sigmoid(x: number, midpoint = 50, steepness = 0.06): number {
  return 1 / (1 + Math.exp(-steepness * (x - midpoint)));
}

/** Exponential power curve: small diffs at bottom, huge gaps at top */
function powerCurve(val: number, exponent = 2.5): number {
  return Math.pow(clamp(val) / 100, exponent);
}

// ============================================================
// LEAGUE CONTEXT — shifts baselines based on overall quality
// ============================================================

export interface LeagueContext {
  avgContact: number; avgBatting: number; avgPower: number;
  avgDiscipline: number; avgSpeed: number;
  avgVelocity: number; avgMovement: number; avgControl: number;
  avgDeception: number;
  avgRange: number; avgGlove: number; avgArm: number;
  batterCount: number; pitcherCount: number;
}

export function computeLeagueContext(allPlayers: SimPlayer[]): LeagueContext {
  const hitters = allPlayers.filter(p => p.is_hitter);
  const pitchers = allPlayers.filter(p => !p.is_hitter);
  const avg = (arr: number[]) => arr.length ? arr.reduce((a, b) => a + b, 0) / arr.length : 50;

  return {
    avgContact: avg(hitters.map(p => p.contact)),
    avgBatting: avg(hitters.map(p => p.battingAttr)),
    avgPower: avg(hitters.map(p => p.power)),
    avgDiscipline: avg(hitters.map(p => p.discipline)),
    avgSpeed: avg(hitters.map(p => p.speed)),
    avgVelocity: avg(pitchers.map(p => p.velocity)),
    avgMovement: avg(pitchers.map(p => p.movement)),
    avgControl: avg(pitchers.map(p => p.control)),
    avgDeception: avg(pitchers.map(p => p.deception)),
    avgRange: avg(allPlayers.map(p => p.range)),
    avgGlove: avg(allPlayers.map(p => p.glove)),
    avgArm: avg(allPlayers.map(p => p.arm)),
    batterCount: hitters.length,
    pitcherCount: pitchers.length,
  };
}

// ============================================================
// EFFECTIVE RATINGS — deception amplifies everything
// ============================================================

interface EffectivePitcher {
  velocity: number; movement: number; control: number;
  deception: number; holdRunner: number;
}

function effectivePitcher(p: SimPlayer): EffectivePitcher {
  const dec = clamp(p.deception);
  return {
    velocity: clamp(p.velocity) * (1 + dec / 400),
    movement: clamp(p.movement) * (1 + dec / 300),
    control: clamp(p.control) * (1 + dec / 400),
    deception: dec,
    holdRunner: clamp(p.hold_runner),
  };
}

// ============================================================
// PITCH SIMULATION
// ============================================================

interface SimPitch {
  inZone: boolean; vel: number; mov: number; ctl: number; dec: number;
}

function throwPitch(pitcher: SimPlayer, fatigue: number, league: LeagueContext): SimPitch {
  const eff = effectivePitcher(pitcher);
  const stam = pitcher.current_stamina ?? 70;
  const fatMod = 1 - fatigue * 0.15;

  const vel = eff.velocity * fatMod;
  const mov = eff.movement * fatMod;
  const ctl = eff.control * (1 - fatigue * 0.10);

  // In zone? — control + deception (deceptive = harder to call, stays "in zone" perception-wise)
  const zoneChance = 0.50 + clamp(ctl) / 200 + eff.deception / 800;
  const inZone = Math.random() < Math.min(0.78, Math.max(0.35, zoneChance));

  return { inZone, vel, mov, ctl, dec: eff.deception };
}

// ============================================================
// BATTER DECISION — swing or take?
// ============================================================

function decideSwing(pitch: SimPitch, batter: SimPlayer, hasRISP: boolean): { swings: boolean; chases: boolean } {
  const disc = clamp(batter.discipline);
  const intel = clamp(batter.intelligence);
  // Intelligence helps recognize pitches; discipline reduces chasing
  const eye = disc / 500 + intel / 500;
  // Clutch: in high-leverage spots (RISP), clutch batters are more disciplined,
  // less clutch batters chase more (press under pressure)
  const clutchMod = hasRISP ? (clamp(batter.clutch) - 50) / 800 : 0;

  const swings = pitch.inZone
    ? Math.random() < (0.72 + eye * 0.20 + clutchMod)
    : Math.random() < (0.28 - eye * 0.18 + pitch.dec / 500 - clutchMod);

  return { swings, chases: !pitch.inZone && swings };
}

// ============================================================
// CONTACT CHECK
// ============================================================

function makesContact(pitch: SimPitch, batter: SimPlayer, fatigue: number, hasRISP: boolean): boolean {
  // Consistency adds variance: low-consistency batters are boom-or-bust (high stddev around their contact)
  const consistencyFactor = 0.7 + (clamp(batter.consistency) / 100) * 0.6; // 0.70 to 1.30
  const effectiveContact = clamp(batter.contact) * consistencyFactor;

  const velPenalty = ((pitch.vel - 65) / 100) * 0.10;
  const movPenalty = (pitch.mov / 100) * 0.06;
  const fatPenalty = fatigue * 0.06;

  // Clutch: in pressure spots, high-clutch batters make better contact
  const clutchBonus = hasRISP ? (clamp(batter.clutch) - 50) / 600 : 0;

  const base = 0.58 + (effectiveContact / 100) * 0.18 + clutchBonus;
  return Math.random() < Math.max(0.22, base - velPenalty - movPenalty - fatPenalty);
}

// ============================================================
// FOUL BALL (contact but not fair — extends at-bat)
// ============================================================

function isFoul(batter: SimPlayer, pitch: SimPitch): boolean {
  const c = clamp(batter.contact);
  const base = 0.28 + (c / 100) * 0.25;
  return Math.random() < (pitch.inZone ? base : base - 0.08);
}

// ============================================================
// BATTED BALL — exit velocity, launch angle, spray, distance
// ============================================================

interface BattedBall {
  type: 'ground' | 'line' | 'fly' | 'popup' | 'home_run';
  exitVelocity: number; launchAngle: number;
  sprayDirection: number; distance: number;
  hitQuality: number;
}

function simulateBattedBall(
  batter: SimPlayer, pitch: SimPitch, strategy: TeamStrategy, league: LeagueContext,
  hasRISP: boolean = false
): BattedBall {
  const power = clamp(batter.power);
  const battingAttr = clamp(batter.battingAttr);
  const speed = clamp(batter.speed);
  const approachMod = (strategy.hittingApproach - 50) / 500;

  // Clutch: boosts exit velocity in RISP situations
  const clutchEVBonus = hasRISP ? (clamp(batter.clutch) - 50) / 100 * 2.5 : 0;

  // EXIT VELOCITY: power=50 → ~90mph (MLB avg), power=80 → ~100mph, power=99 → ~110mph
  // Using (power/100)^1.1 curve for more HR potential across the board
  const evCurve = Math.pow(clamp(power) / 100, 1.1);
  const rawEV = 78 + evCurve * 32 + battingAttr * 0.06
    + (pitch.vel - 75) * 0.05 + approachMod * 4 + clutchEVBonus;
  const exitVelocity = clamp(rawEV + (Math.random() - 0.5) * 12, 60, 118);

  // LAUNCH ANGLE: realistic distribution -5° to 45°, centered around 12-18°
  // Power hitters elevate more (higher base angle), movement pitchers suppress it
  // ±20° variance to get realistic spread (grounders, liners, flies, popups)
  const baseAngle = 8 + (power - 50) * 0.15 + battingAttr * 0.04;
  const velEffect = (pitch.vel - 80) * 0.05;   // high vel → swing under → higher angle (popups)
  const movEffect = (pitch.mov - 50) * 0.06;   // high movement → grounders
  const launchAngle = baseAngle - velEffect - movEffect + (Math.random() - 0.5) * 40;

  // Ball type from launch angle
  let type: BattedBall['type'];
  if (launchAngle < 4) type = 'ground';
  else if (launchAngle < 15) type = 'line';
  else if (launchAngle < 32) type = 'fly';
  else type = 'popup';

  // Spray direction: power hitters pull, deception = unpredictable
  const isRHB = batter.batting_hand === 'R' || batter.batting_hand === 'S';
  const baseSpray = isRHB ? 45 : 135;
  const pullTendency = (power - 50) * 0.30;
  const lateSwing = (pitch.vel - 75) * 0.4;
  const deceptionSpread = (pitch.dec / 100) * 10;
  const sprayDirection = clamp(
    baseSpray + (Math.random() - 0.5) * 80 - pullTendency + lateSwing + (Math.random() - 0.5) * deceptionSpread,
    0, 180
  );

  // DISTANCE: realistic physics
  // At optimal angle (26°): dist = EV * 4.8 - 130
  //   EV=90 → 302ft (gap), EV=98 → 340ft (warning track), EV=105 → 374ft (HR)
  // Angle modifier: penalty for being far from optimal
  const optimalAngle = 26;
  const angleMod = 1.0 - Math.abs(launchAngle - optimalAngle) * 0.012;
  const distance = (exitVelocity * 4.8 - 130) * Math.max(0.5, angleMod)
    + (Math.random() - 0.5) * 25;

  // HR: ball goes 360ft+ and it's a fly (not grounder/popup) - ~1.2 HR/team/game target
  if (distance > 360 && type !== 'ground' && type !== 'popup') type = 'home_run';

  // Hit quality for visualizer
  const hitQuality = Math.min(100, Math.max(0,
    (exitVelocity - 70) * 1.4
    + (type === 'home_run' ? 30 : type === 'line' ? 12 : type === 'fly' ? 4 : -8)
  ));

  return { type, exitVelocity, launchAngle, sprayDirection, distance, hitQuality };
}

// ============================================================
// FIELDING — 3-gate system: Reach (Range) → Handle (Glove) → Convert
// ============================================================

interface FielderResult {
  playerId: string | null; position: string | null;
  canReach: boolean; catchesIt: boolean;
  isError: boolean;
}

function fieldBall(
  ball: BattedBall,
  homeFielders: Map<string, { id: string; position: string; range: number; glove: number; arm: number }>,
  awayFielders: Map<string, { id: string; position: string; range: number; glove: number; arm: number }>,
  isHomeFielding: boolean,
  league: LeagueContext
): FielderResult {
  const fielders = isHomeFielding ? awayFielders : homeFielders;

  // Map spray angle to position
  let primaryPos: string;
  if (ball.type === 'ground' || ball.type === 'line') {
    if (ball.sprayDirection < 25) primaryPos = '3B';
    else if (ball.sprayDirection < 55) primaryPos = 'SS';
    else if (ball.sprayDirection < 120) primaryPos = '2B';
    else primaryPos = '1B';
  } else {
    if (ball.sprayDirection < 40) primaryPos = 'LF';
    else if (ball.sprayDirection < 140) primaryPos = 'CF';
    else primaryPos = 'RF';
  }

  // Crossover positions for grounders up the middle
  if (ball.type === 'ground' && ball.sprayDirection > 45 && ball.sprayDirection < 65) {
    // Up the middle — SS or 2B depending on spray
    primaryPos = Math.random() < 0.5 ? 'SS' : '2B';
  }

  const f = fielders.get(primaryPos);
  if (!f) return { playerId: null, position: primaryPos, canReach: true, catchesIt: false, isError: true };

  // GATE 1: Can the fielder reach it? (Range)
  const rangeAdj = (f.range - 50) / 100 * 0.22;
  const baseReach = ball.type === 'popup' ? 0.92
    : ball.type === 'fly' ? 0.78
    : ball.type === 'line' ? 0.55
    : 0.60;
  const reachChance = Math.min(0.98, Math.max(0.15, baseReach + rangeAdj));
  const canReach = Math.random() < reachChance;

  if (!canReach) return { playerId: f.id, position: f.position, canReach: false, catchesIt: false, isError: false };

  // GATE 2: Does he handle it cleanly? (Glove)
  const gloveAdj = (f.glove - 50) / 100 * 0.12;
  // Hard-hit balls are harder to handle
  const evDifficulty = (ball.exitVelocity - 85) / 100 * 0.06;
  const baseHandle = ball.type === 'popup' ? 0.97
    : ball.type === 'fly' ? 0.88
    : ball.type === 'line' ? 0.72
    : 0.80;
  const handleChance = Math.min(0.99, Math.max(0.60, baseHandle + gloveAdj - evDifficulty));
  const catchesIt = Math.random() < handleChance;

  if (!catchesIt) {
    // Error!
    const errorChance = 0.15 + (1 - f.glove / 100) * 0.20;
    return {
      playerId: f.id, position: f.position, canReach: true, catchesIt: false,
      isError: Math.random() < errorChance,
    };
  }

  return { playerId: f.id, position: f.position, canReach: true, catchesIt: true, isError: false };
}

// ============================================================
// RUNNER ADVANCEMENT
// ============================================================

function advanceRunnersOnHit(
  ball: BattedBall, batterId: string,
  runners: { first: string | null; second: string | null; third: string | null },
  batterSpeed: number, outfielderArm: number,  // outfielder of primary fly-ball position
  bsMap: Map<string, BatterLine>, isOut: boolean
): { runs: number; rbis: number } {
  let runs = 0, rbis = 0;
  const score = (id: string | null) => {
    if (!id) return;
    const s = bsMap.get(id);
    if (s) s.runs++;
    runs++; rbis++;
  };

  if (ball.type === 'home_run') {
    score(runners.third); score(runners.second); score(runners.first);
    runners.first = null; runners.second = null; runners.third = null;
    // HR batter scores himself
    const bs = bsMap.get(batterId); if (bs) bs.runs++;
    runs++; rbis++;
  } else if (!isOut) {
    // Ball in play and not caught
    if ((ball.type === 'ground' || ball.type === 'line') || (ball.type === 'ground' && ball.distance < 60)) {
      // Single: 3rd scores, 2nd scores ~60%, 1st to 2nd/3rd
      if (runners.third) score(runners.third);
      if (runners.second) {
        if (Math.random() < 0.55 + (batterSpeed - 50) / 200) score(runners.second);
        else runners.third = runners.second;
        runners.second = null;
      }
      if (runners.first) {
        const firstToSecond = 0.75 + (batterSpeed - 50) / 200;
        if (Math.random() < firstToSecond) runners.second = runners.first;
        else runners.third = runners.first; // aggressive
        runners.first = null;
      }
      runners.first = batterId;
    } else if (ball.type === 'fly') {
      if (runners.third) score(runners.third);
      if (runners.second) score(runners.second);
      if (runners.first) {
        if (Math.random() < 0.50 + (batterSpeed - 50) / 200) score(runners.first);
        else runners.third = runners.first;
      }
      runners.first = null; runners.second = null;
      runners.second = batterId;
    } else if (ball.type === 'popup') {
      if (runners.third) score(runners.third);
      if (runners.second) score(runners.second);
      if (runners.first) score(runners.first);
      runners.first = null; runners.second = null; runners.third = batterId;
    }
  } else if (ball.type === 'fly' || ball.type === 'popup') {
    // Caught fly: runners tag up
    if (runners.third && ball.distance > 80 && ball.type === 'fly') {
      // Check outfielder arm
      const throwChance = 0.30 - (outfielderArm - 50) / 200;
      if (Math.random() > throwChance) score(runners.third);
      else runners.third = null;  // held
    }
  }

  return { runs, rbis };
}

// ============================================================
// STARTING PITCHER SELECTION (5-man rotation)
// ============================================================

function selectStarter(pitchers: SimPlayer[], gameDay: number): SimPlayer {
  const MIN_REST = 4;
  const sps = pitchers.filter(p => p.position === 'P');
  if (sps.length === 0) return pitchers.find(p => !p.is_hitter) || pitchers[0];

  // Sort by endurance desc (starters have high endurance), then overall as tiebreaker
  const byEndurance = [...sps].sort((a, b) => {
    const eDiff = (b.endurance || 50) - (a.endurance || 50);
    if (Math.abs(eDiff) > 5) return eDiff;
    return (b.overall || 50) - (a.overall || 50);
  });
  const rotationSize = Math.min(5, byEndurance.length);
  const slot = (gameDay - 1) % rotationSize;
  const slotted = byEndurance[slot];

  // Check if slotted starter is rested enough
  if (slotted && (!slotted.last_game_day || (gameDay - slotted.last_game_day) >= MIN_REST)) {
    return slotted;
  }

  // Slotted guy is tired — find next rested starter in rotation
  const restedRotation = byEndurance.slice(0, rotationSize).filter(p =>
    !p.last_game_day || (gameDay - p.last_game_day) >= MIN_REST
  );
  if (restedRotation.length) return restedRotation[0];

  // Emergency: any rested pitcher
  const anyRested = sps.filter(p =>
    !p.last_game_day || (gameDay - p.last_game_day) >= MIN_REST
  );
  if (anyRested.length) return anyRested.sort((a, b) => (b.endurance || 0) - (a.endurance || 0))[0];

  // Absolute emergency: most rested
  return [...sps].sort((a, b) =>
    (a.last_game_day || 0) - (b.last_game_day || 0)
  )[0];
}

// ============================================================
// PITCHING CHANGE LOGIC
// ============================================================

function shouldPullPitcher(
  pitcher: PitcherLine, pitcherPlayer: SimPlayer, inning: number, isStarter: boolean,
  mgmtMod: number = 0  // -1.0 (quick hook) to +1.0 (ride deep)
): boolean {
  const stamina = pitcherPlayer.current_stamina ?? 70;
  const enduranceRating = pitcherPlayer.endurance ?? 50; // 0-100 endurance attribute
  const durability = clamp(pitcherPlayer.durability ?? 50);
  const durabilityBonus = (durability - 50) / 50 * 15;
  // pitcherManagement modifier: low = pull 10 pitches earlier; high = let them go 10 more
  const mgmtBonus = mgmtMod * 10;

  // Endurance scales max pitches continuously instead of binary starter/reliever
  // 80 END → ~100 pitches, 50 END → ~65, 20 END → ~35
  const endScale = 0.3 + (enduranceRating / 100) * 0.7; // 0.3–1.0
  const maxPitches = (100 * endScale) + durabilityBonus * endScale + mgmtBonus * endScale;
  const maxE = 30 + enduranceRating * 0.7; // 30–100 scaled by endurance
  const endurance = maxE * (0.6 + (stamina / 100) * 0.8);

  const pitchesThrown = pitcher.pitchesThrown;
  const pitchFatigue = pitchesThrown / maxPitches;
  const staminaPct = endurance > 0 ? 1 - pitchFatigue : 0;

  // Quick hook managers pull earlier when getting shelled
  const shelledThreshold = enduranceRating >= 50
    ? Math.max(3, 5 - Math.round(mgmtMod * -1)) // starter/long relief threshold
    : Math.max(1, 3 - Math.round(mgmtMod * -0.5)); // short relief threshold
  const isGettingShelled = pitcher.runsAllowed >= shelledThreshold;
  const pullThreshold = enduranceRating >= 50 ? 0.30 : 0.25;

  if (pitchesThrown >= maxPitches || staminaPct < pullThreshold || isGettingShelled) {
    if (isStarter && pitcher.outsRecorded < 15 && pitcher.runsAllowed < shelledThreshold) return false;
    return true;
  }
  return false;
}

// ============================================================
// STEAL ATTEMPT
// ============================================================

function attemptSteal(
  runner: SimPlayer, catcher: SimPlayer | undefined, pitcher: SimPlayer,
): { success: boolean } {
  const speed = clamp(runner.speed);
  const catcherArm = catcher ? clamp(catcher.arm) : 50;
  const pitcherHR = clamp(pitcher.hold_runner);

  // Steal success = runner speed vs (catcher arm + pitcher hold runner)
  const baseRate = 0.55;
  const speedBonus = ((speed - 50) / 100) * 0.25;
  const catcherReduction = ((catcherArm - 50) / 100) * 0.12;
  const pitcherReduction = ((pitcherHR - 50) / 100) * 0.10;

  const successRate = baseRate + speedBonus - catcherReduction - pitcherReduction;
  return { success: Math.random() < Math.max(0.30, Math.min(0.85, successRate)) };
}

// ============================================================
// MAIN GAME SIMULATION
// ============================================================

const DEFAULT_STRATEGY: TeamStrategy = {
  stealAttempts: 50, sacrificeBunts: 50, hittingApproach: 50,
  baseRunningAggression: 50, intentionalWalks: 50,
  pitcherManagement: 50, shiftFrequency: 50,
  bullpenUsage: 50, defenseFirst: 50, lineupProtection: 50,
};

export function simulateGame(
  homePlayers: SimPlayer[], awayPlayers: SimPlayer[],
  homeTeam: { id: string; city: string; name: string; strategy?: TeamStrategy },
  awayTeam: { id: string; city: string; name: string; strategy?: TeamStrategy },
  gameId: string, gameDay: number
): GameResult {
  if (!homePlayers.length || !awayPlayers.length) {
    console.warn(`⚠️ SimEngineV2: Empty roster! home=${homePlayers.length} players, away=${awayPlayers.length} players. Game ${gameId} skipped.`);
    return { homeScore: 0, awayScore: 0, homeHits: 0, awayHits: 0, homeErrors: 0, awayErrors: 0,
      innings: 0, lineScore: [], homeBatters: [], awayBatters: [], homePitchers: [], awayPitchers: [],
      homeFielding: [], awayFielding: [], winningPitcherId: null, losingPitcherId: null,
      savePitcherId: null, atBats: [] };
  }

  const allPlayers = [...homePlayers, ...awayPlayers];
  const league = computeLeagueContext(allPlayers);

  const homeStrategy = { ...DEFAULT_STRATEGY, ...(homeTeam.strategy || {}) };
  const awayStrategy = { ...DEFAULT_STRATEGY, ...(awayTeam.strategy || {}) };

  // Starting pitchers
  const homePitchers = homePlayers.filter(p => !p.is_hitter);
  const awayPitchers = awayPlayers.filter(p => !p.is_hitter);
  const homeHitters = homePlayers.filter(p => p.is_hitter);
  const awayHitters = awayPlayers.filter(p => p.is_hitter);

  const homeStarter = selectStarter(homePitchers, gameDay);
  const awayStarter = selectStarter(awayPitchers, gameDay);

  // Build lineups (top 9 by composite rating)
  const buildLineup = (hitters: SimPlayer[]) =>
    hitters.filter(h => h.position !== 'P')
      .sort((a, b) => {
        const aR = clamp(a.battingAttr) * 0.35 + clamp(a.contact) * 0.30 + clamp(a.power) * 0.25 + clamp(a.speed) * 0.10;
        const bR = clamp(b.battingAttr) * 0.35 + clamp(b.contact) * 0.30 + clamp(b.power) * 0.25 + clamp(b.speed) * 0.10;
        return bR - aR;
      }).slice(0, 9);

  const homeL = buildLineup(homeHitters);
  const awayL = buildLineup(awayHitters);
  const homeBench = homeHitters.filter(h => h.position !== 'P' && !homeL.some(l => l.id === h.id)).slice(0, 5);
  const awayBench = awayHitters.filter(h => h.position !== 'P' && !awayL.some(l => l.id === h.id)).slice(0, 5);

  // Build fielder maps
  const makeFielderMap = (team: SimPlayer[]) => {
    const map = new Map<string, { id: string; position: string; range: number; glove: number; arm: number }>();
    const positions = ['C','1B','2B','3B','SS','LF','CF','RF'];
    const used = new Set<string>();
    for (const pos of positions) {
      const avail = team.filter(p => p.is_hitter && !used.has(p.id) && p.position !== 'P');
      const best = avail.sort((a, b) => {
        // Assign by position preference: range up middle, glove at corners, arm at C/RF/3B
        const aScore = pos === 'SS' || pos === 'CF' || pos === '2B'
          ? (clamp(a.range) * 0.6 + clamp(a.glove) * 0.3)
          : pos === 'C' || pos === 'RF' || pos === '3B'
          ? (clamp(a.arm) * 0.4 + clamp(a.glove) * 0.5)
          : (clamp(a.glove) * 0.6 + clamp(a.range) * 0.3);
        const bScore = pos === 'SS' || pos === 'CF' || pos === '2B'
          ? (clamp(b.range) * 0.6 + clamp(b.glove) * 0.3)
          : pos === 'C' || pos === 'RF' || pos === '3B'
          ? (clamp(b.arm) * 0.4 + clamp(b.glove) * 0.5)
          : (clamp(b.glove) * 0.6 + clamp(b.range) * 0.3);
        return bScore - aScore;
      })[0];
      if (best) { map.set(pos, { id: best.id, position: pos, range: clamp(best.range), glove: clamp(best.glove), arm: clamp(best.arm) }); used.add(best.id); }
    }
    return map;
  };

  const homeFielders = makeFielderMap(homePlayers);
  const awayFielders = makeFielderMap(awayPlayers);

  // Game state
  let homeScore = 0, awayScore = 0;
  let homeHits = 0, awayHits = 0, homeErrors = 0, awayErrors = 0;
  const lineScore: { inning: number; homeRuns: number; awayRuns: number }[] = [];
  const runners = { first: null as string | null, second: null as string | null, third: null as string | null };
  const countRunners = () => (runners.first ? 1 : 0) + (runners.second ? 1 : 0) + (runners.third ? 1 : 0);
  const atBats: PlayByPlayAtBat[] = [];

  // Batter stat maps
  const homeBS = new Map<string, BatterLine>();
  const awayBS = new Map<string, BatterLine>();
  const mkBatterLine = (p: SimPlayer, teamId: string): BatterLine => ({
    playerId: p.id, playerName: `${p.first_name} ${p.last_name}`, position: p.position, teamId,
    plateAppearances: 0, atBats: 0, runs: 0, hits: 0, doubles: 0, triples: 0, homeRuns: 0,
    rbi: 0, walks: 0, strikeouts: 0, stolenBases: 0, caughtStealing: 0,
    hitByPitch: 0, sacrificeFlies: 0, sacrificeHits: 0, groundedIntoDP: 0,
  });
  homeL.forEach(p => homeBS.set(p.id, mkBatterLine(p, homeTeam.id)));
  awayL.forEach(p => awayBS.set(p.id, mkBatterLine(p, awayTeam.id)));

  // Pitcher stat maps
  const mkPitcherLine = (p: SimPlayer, isStarter: boolean): PitcherLine => ({
    playerId: p.id, playerName: `${p.first_name} ${p.last_name}`, teamId: p.team_id,
    inningsPitched: 0, battersFaced: 0, pitchesThrown: 0, hitsAllowed: 0,
    runsAllowed: 0, earnedRuns: 0, walksAllowed: 0, strikeoutsPitched: 0,
    homeRunsAllowed: 0, win: false, loss: false, save: false,
    outsRecorded: 0, isStarter,
  });
  const homePS: PitcherLine[] = [mkPitcherLine(homeStarter, true)];
  const awayPS: PitcherLine[] = [mkPitcherLine(awayStarter, true)];

  // Fielder stat maps
  const homeFL = new Map<string, FielderLine>();
  const awayFL = new Map<string, FielderLine>();
  homeFielders.forEach((f, pos) => homeFL.set(f.id, { playerId: f.id, playerName: '', position: pos, teamId: homeTeam.id, putouts: 0, assists: 0, errors: 0, doublePlays: 0 }));
  awayFielders.forEach((f, pos) => awayFL.set(f.id, { playerId: f.id, playerName: '', position: pos, teamId: awayTeam.id, putouts: 0, assists: 0, errors: 0, doublePlays: 0 }));

  const getPitcher = (isHome: boolean) => isHome ? awayPS[awayPS.length - 1] : homePS[homePS.length - 1];
  const getPitcherPlayer = (isHome: boolean): SimPlayer | undefined => {
    const pl = getPitcher(isHome);
    return (isHome ? awayPlayers : homePlayers).find(p => p.id === pl.playerId);
  };

  // === SIMULATE HALF-INNING ===
  const simHalfInning = (
    lineup: SimPlayer[], bench: SimPlayer[],
    bsMap: Map<string, BatterLine>, flMap: Map<string, FielderLine>,
    pLines: PitcherLine[], isHome: boolean
  ) => {
    let outs = 0, lineupIdx = 0;
    const strat = isHome ? homeStrategy : awayStrategy;
    const opponentFielders = isHome ? awayFielders : homeFielders;
    const opponentFL = isHome ? awayFL : homeFL;

    let safetyCount = 0;
    while (outs < 3 && safetyCount < 15) {
      safetyCount++;
      const batter = lineup[lineupIdx % lineup.length];
      const bStat = bsMap.get(batter.id);
      if (!bStat) { lineupIdx++; continue; }

      const pitcher = getPitcher(isHome);
      const pitcherPlayer = getPitcherPlayer(isHome);
      if (!pitcherPlayer) { outs = 3; break; }

      const stamina = pitcherPlayer.current_stamina ?? 70;
      const pitchesSoFar = pitcher.pitchesThrown;
      const fatigue = Math.max(0, (pitchesSoFar - 60) / 80) * Math.max(0, 1 - stamina / 120);

      // === STEAL ATTEMPT ===
      if (outs < 2 && runners.first && strat.stealAttempts > 35) {
        const stealChance = ((strat.stealAttempts - 35) / 100) * 0.06;
        if (Math.random() < stealChance) {
          const runnerPlayer = (isHome ? homePlayers : awayPlayers).find(p => p.id === runners.first);
          if (runnerPlayer) {
            const result = attemptSteal(runnerPlayer,
              isHome ? awayPlayers.find(p => awayFielders.get('C')?.id === p.id) : homePlayers.find(p => homeFielders.get('C')?.id === p.id),
              pitcherPlayer);
            if (result.success) {
              runners.second = runners.first; runners.first = null;
              bStat.stolenBases++;
            } else {
              outs++; runners.first = null;
              bStat.caughtStealing++;
            }
          }
          if (outs >= 3) { lineupIdx++; continue; }
        }
      }

      // === SACRIFICE BUNT ===
      if (strat.sacrificeBunts > 40 && (runners.first || runners.second) && outs < 2) {
        const buntChance = ((strat.sacrificeBunts - 40) / 100) * 0.04 * (batter.speed < 70 ? 1.3 : 0.3);
        if (Math.random() < buntChance) {
          bStat.plateAppearances++;
          bStat.sacrificeHits++;
          outs++;
          if (runners.second) { runners.third = runners.second; runners.second = runners.first || null; runners.first = null; }
          else if (runners.first) { runners.second = runners.first; runners.first = null; }
          lineupIdx++; continue;
        }
      }

      // === AT-BAT SIMULATION ===
      // Hybrid approach: W/K resolved probabilistically (accurate rates),
      // fair balls resolved with physics (realistic play-by-play).
      const hasRISP = runners.second !== null || runners.third !== null;
      bStat.plateAppearances++;
      pitcher.pitchesThrown += 4; // avg pitches per PA (approx)
      pitcher.battersFaced++;

      // --- HBP: ~0.9% base, higher vs wild pitchers ---
      const hbpChance = 0.008 + (60 - clamp(pitcherPlayer.control)) / 100 * 0.004;
      if (Math.random() < Math.max(0, hbpChance)) {
        bStat.hitByPitch++;
        pitcher.walksAllowed++;
        advanceOnWalk(batter.id, runners, bsMap);
        lineupIdx++;
        continue;
      }

      // --- INTENTIONAL WALK: manager puts dangerous hitter on base intentionally ---
      // Only with 1st base open, runner on 2nd or 3rd, < 2 outs, late innings
      const isLateInning = false; // passed in from inning context — approximated here
      const ibaseOpen = !runners.first;
      const runnerInScoring = runners.second || runners.third;
      if (ibaseOpen && runnerInScoring && outs < 2 && strat.intentionalWalks > 60) {
        // Higher intentionalWalks + power batter = more IBBs
        const ibbChance = ((strat.intentionalWalks - 60) / 100) * 0.06
          * (clamp(batter.power) / 100);   // only bother for power hitters
        if (Math.random() < ibbChance) {
          bStat.walks++;
          pitcher.walksAllowed++;
          advanceOnWalk(batter.id, runners, bsMap);
          lineupIdx++;
          continue;
        }
      }

      // --- WALK: driven by pitcher control + batter discipline ---
      // control=50,disc=50 → ~8.2%; ace (ctl=76) → ~5.6%; patient hitter (disc=70) → ~10.6%
      const walkProb = 0.082
        - (clamp(pitcherPlayer.control) - 50) * 0.001
        + (clamp(batter.discipline) - 50) * 0.0012
        + (clamp(batter.intelligence) - 50) * 0.0004  // smart batters work counts better
        + (hasRISP ? (clamp(batter.clutch) - 50) * 0.0003 : 0); // clutch = patient with RISP
      if (Math.random() < Math.max(0.02, Math.min(0.18, walkProb))) {
        bStat.walks++;
        pitcher.walksAllowed++;
        advanceOnWalk(batter.id, runners, bsMap);
        lineupIdx++;
        continue;
      }

      // --- STRIKEOUT: velocity + movement vs contact + discipline ---
      // avg K% ~22%; ace (vel=78,mov=72) vs avg → ~27%; bad pitcher vs avg → ~19%
      const kProb = 0.224
        + (clamp(pitcherPlayer.velocity) - 50) * 0.0015
        + (clamp(pitcherPlayer.movement) - 50) * 0.0012
        - (clamp(batter.contact) - 50) * 0.0018
        - (clamp(batter.discipline) - 50) * 0.0004
        + (hasRISP ? (clamp(batter.clutch) - 50) * -0.0004 : 0); // clutch = fewer Ks with RISP
      bStat.atBats++;
      if (Math.random() < Math.max(0.08, Math.min(0.42, kProb))) {
        bStat.strikeouts++;
        pitcher.strikeoutsPitched++;
        outs++;
        lineupIdx++;
        continue;
      }

      // --- FAIR BALL: physics-based batted ball ---
      const pitch = throwPitch(pitcherPlayer, fatigue, league);
      const ball = simulateBattedBall(batter, pitch, strat, league, hasRISP);
      // Defensive shift: fielding team's shiftFrequency reduces grounder hit rate for pull hitters
      // Pull hitters (power>55, RHB) are shifted most; shift hurts them on grounders, not flies
      const defensiveStrat = isHome ? awayStrategy : homeStrategy;
      const shiftBonus = (ball.type === 'ground' && clamp(batter.power) > 55)
        ? (defensiveStrat.shiftFrequency - 50) / 100 * 0.06  // up to 3% fewer hits on grounders
        : 0;
      const fr = fieldBall(ball, homeFielders, awayFielders, isHome, league);

      let playType: string;
      let isOut = false;

      if (ball.type === 'home_run') {
        playType = 'home_run';
      } else if (!fr.playerId || !fr.canReach) {
        playType = (ball.type === 'ground' || ball.type === 'line') ? 'single'
          : ball.distance > 320 ? 'triple' : 'double';
      } else if (fr.catchesIt || (ball.type === 'ground' && Math.random() < shiftBonus)) {
        // shiftBonus: shifted team converts more grounders to outs
        isOut = true;
        if (ball.type === 'ground') {
          playType = 'groundout';
          // GIDP: runner on 1st, < 2 outs, high movement pitcher
          if (runners.first && outs < 2 && Math.random() < 0.18 + (clamp(pitcherPlayer.movement) - 50) / 500) {
            bStat.groundedIntoDP++; outs++;
            runners.first = null;
          }
        } else if (ball.type === 'line') { playType = 'lineout'; }
        else if (ball.type === 'fly') { playType = 'flyout'; }
        else { playType = 'popout'; }

        // Sac fly: runner on 3rd scores on fly out (not an AB)
        if (runners.third && ball.type === 'fly' && outs <= 2) {
          const outfielderArm = (fr.position === 'LF' || fr.position === 'CF' || fr.position === 'RF')
            ? (isHome ? awayFielders.get(fr.position)?.arm || 50 : homeFielders.get(fr.position)?.arm || 50)
            : 50;
          const tagSuccessChance = 0.78 - (clamp(outfielderArm) - 50) / 250;
          if (Math.random() < tagSuccessChance) {
            score(runners.third);
            runners.third = null;
            bStat.sacrificeFlies++;
            bStat.rbi++;
            bStat.atBats--; // sac fly is NOT an AB
            pitcher.runsAllowed++;
            pitcher.earnedRuns++;
          }
        }

        // Credit putout
        if (fr.playerId) {
          const fl = flMap.get(fr.playerId) || opponentFL.get(fr.playerId);
          if (fl) fl.putouts++;
        }
      } else if (fr.isError) {
        playType = 'error';
        if (isHome) awayErrors++; else homeErrors++;
        if (fr.playerId) { const fl = opponentFL.get(fr.playerId); if (fl) fl.errors++; }
        runners.first = batter.id;
      } else {
        playType = (ball.type === 'ground' || ball.type === 'line') ? 'single' : 'double';
      }

      // --- Apply outcome ---
      let runsScored = 0;
      if (playType === 'home_run') {
        bStat.hits++;
        bStat.homeRuns++;
        if (isHome) homeHits++; else awayHits++;
        const rr = countRunners() + 1;
        bStat.rbi += rr;
        runsScored = rr;
        pitcher.hitsAllowed++;
        pitcher.homeRunsAllowed++;
        pitcher.runsAllowed += rr;
        pitcher.earnedRuns += rr;
        scoreAllRunners();
        runners.first = null; runners.second = null; runners.third = null;
      } else if (playType === 'single' || playType === 'double' || playType === 'triple') {
        bStat.hits++;
        if (playType === 'double') bStat.doubles++;
        if (playType === 'triple') bStat.triples++;
        if (isHome) homeHits++; else awayHits++;
        pitcher.hitsAllowed++;
        // baseRunningAggression: higher = more likely to take extra bases
        const baseRunMod = (strat.baseRunningAggression - 50) / 100; // -0.5 to +0.5
        const r = advanceOnHit(ball, batter.id, runners, batter.speed, 50, bsMap, false, baseRunMod);
        runsScored = r.runs;
        bStat.rbi += r.rbis;
        pitcher.runsAllowed += runsScored;
        pitcher.earnedRuns += runsScored;
        // Place batter on base (advanceOnHit sets runners above)
        if (playType === 'single') runners.first = batter.id;
        else if (playType === 'double') runners.second = batter.id;
        else runners.third = batter.id;
      } else if (isOut) {
        outs++;
      }
      // 'error': batter on first, no hit, already set above

      // PBP record
      atBats.push({
        batter: { id: batter.id, name: batter.name || `${batter.first_name} ${batter.last_name}`, position: batter.position },
        pitcher: { id: pitcher.playerId, name: pitcher.playerName },
        pitch: { velocity: Math.round(pitch.vel), movement: Math.round(pitch.mov), inZone: pitch.inZone },
        decision: 'swing',
        result: { type: playType, exitVelocity: Math.round(ball.exitVelocity), launchAngle: Math.round(ball.launchAngle), sprayDirection: Math.round(ball.sprayDirection), distance: Math.round(ball.distance), fielder: fr.position },
        runsScored,
        baseRunnersAfter: { ...runners }, outsAfter: outs,
      });

      // Pitching change check
      if (outs === 0 || outs >= 3) {
        const curPitcher = getPitcher(isHome);
        const curPlayer = getPitcherPlayer(isHome);
        // pitcherManagement: low (0) = quick hook, high (100) = ride starter deep
        // Affects the pitch limit and pull thresholds inside shouldPullPitcher
        const mgmtMod = (strat.pitcherManagement - 50) / 50; // -1.0 to +1.0
        if (curPlayer && shouldPullPitcher(curPitcher, curPlayer, Math.ceil(outs / 3) + 1, curPitcher.isStarter, mgmtMod)) {
          const bullpen = (isHome ? awayPlayers : homePlayers)
            .filter(p => !p.is_hitter && p.id !== curPitcher.playerId && p.position === 'P' && !p.is_starter);
          if (bullpen.length > 0) {
            // bullpenUsage: high = bring in best reliever early; low = save ace for 9th
            const bulMod = strat.bullpenUsage;
            const newP = bullpen.sort((a, b) =>
              ((b.velocity || 0) + (b.control || 0) + (b.movement || 0)) -
              ((a.velocity || 0) + (a.control || 0) + (a.movement || 0))
            )[bulMod > 60 ? 0 : bullpen.length - 1]; // high usage = best arm now; low = save best for later
            pLines.push(mkPitcherLine(newP, false));
          }
        }
      }

      lineupIdx++;
    }

    getPitcher(isHome).outsRecorded += 3;

    function score(id: string | null) { if (id) { const s = bsMap.get(id); if (s) s.runs++; if (isHome) homeScore++; else awayScore++; } }
    function scoreAllRunners() { score(runners.first); score(runners.second); score(runners.third); }
    function advanceOnWalk(id: string, r: typeof runners, map: Map<string, BatterLine>) {
      if (r.first && r.second && r.third) { score(r.third); r.third = r.second; r.second = r.first; r.first = id; }
      else if (r.first && r.second) { r.third = r.second; r.second = r.first; r.first = id; }
      else if (r.first) { r.second = r.first; r.first = id; }
      else r.first = id;
    }
    function advanceOnHit(ball: BattedBall, id: string, r: typeof runners, spd: number, arm: number, map: Map<string, BatterLine>, isOut: boolean, baseRunMod: number = 0) {
      let runs = 0, rbis = 0;
      const sc = (rid: string | null) => { if (!rid) return; const s = map.get(rid); if (s) s.runs++; if (isHome) homeScore++; else awayScore++; runs++; rbis++; };
      // baseRunMod: -0.5 (conservative) to +0.5 (aggressive) — shifts all advance probabilities
      const br = baseRunMod * 0.12; // scale effect: ±6% on each advance decision
      if ((ball.type === 'ground' || ball.type === 'line')) {
        if (r.third) sc(r.third);
        if (r.second) { if (Math.random() < 0.50 + (spd - 50) / 200 + br) sc(r.second); else r.third = r.second; r.second = null; }
        if (r.first) { if (Math.random() < 0.70 + (spd - 50) / 250 + br) r.second = r.first; else r.third = r.first; r.first = null; }
        r.first = id;
      } else if (ball.type === 'fly') {
        if (r.third) sc(r.third);
        if (r.second) sc(r.second);
        if (r.first) { if (Math.random() < 0.45 + (spd - 50) / 200 + br) sc(r.first); else r.third = r.first; r.first = null; }
        r.second = id;
      } else if (ball.type === 'popup') {
        if (r.third) sc(r.third);
        if (r.second) sc(r.second);
        if (r.first) sc(r.first); r.first = null; r.second = null; r.third = id;
      }
      return { runs, rbis };
    }
  };

  // === PLAY 9 INNINGS ===
  for (let inning = 1; inning <= 9; inning++) {
    const awayBefore = awayScore;
    simHalfInning(awayL, awayBench, awayBS, awayFL, awayPS, false);
    const homeBefore = homeScore;
    simHalfInning(homeL, homeBench, homeBS, homeFL, homePS, true);
    lineScore.push({ inning, homeRuns: homeScore - homeBefore, awayRuns: awayScore - awayBefore });
    if (inning === 9 && homeScore > awayScore) break;
  }

  // === EXTRA INNINGS (no ties!) ===
  let extraInning = 10;
  while (homeScore === awayScore && extraInning <= 18) {
    const awayBefore = awayScore;
    simHalfInning(awayL, awayBench, awayBS, awayFL, awayPS, false);
    if (homeScore > awayScore) break;
    const homeBefore = homeScore;
    simHalfInning(homeL, homeBench, homeBS, homeFL, homePS, true);
    lineScore.push({ inning: extraInning, homeRuns: homeScore - homeBefore, awayRuns: awayScore - awayBefore });
    extraInning++;
  }
  // Hard cap at 18 - if still tied, home wins via walk-off
  if (homeScore === awayScore) homeScore++;

  // === W/L/S decisions ===
  const winner = homeScore > awayScore ? 'home' : 'away';
  const winPS = winner === 'home' ? homePS : awayPS;
  const losePS = winner === 'home' ? awayPS : homePS;
  let winId: string | null = null, lossId: string | null = null;

  const starter = winPS.find(p => p.isStarter);
  if (starter && starter.outsRecorded >= 15 && starter.runsAllowed <= 3) {
    starter.win = true; winId = starter.playerId;
  } else {
    const relievers = winPS.filter(p => !p.isStarter);
    if (relievers.length > 0) { relievers.sort((a, b) => a.runsAllowed - b.runsAllowed)[0].win = true; winId = relievers[0].playerId; }
  }

  if (losePS.length > 0) {
    const loser = losePS.find(p => p.isStarter) || losePS[0];
    loser.loss = true; lossId = loser.playerId;
  }

  const lead = Math.abs(homeScore - awayScore);
  if (lead <= 3 && winPS.length > 1) {
    const closer = winPS.filter(p => !p.isStarter)[winPS.filter(p => !p.isStarter).length - 1];
    if (closer && closer.outsRecorded >= 3 && closer.runsAllowed === 0) { closer.save = true; }
  }

  // Format IPs
  [...homePS, ...awayPS].forEach(p => {
    p.inningsPitched = Math.floor(p.outsRecorded / 3) + (p.outsRecorded % 3) * 0.1;
  });

  return {
    homeScore, awayScore, homeHits: Math.max(0, homeHits), awayHits: Math.max(0, awayHits),
    homeErrors, awayErrors, innings: lineScore.length, lineScore,
    homeBatters: Array.from(homeBS.values()), awayBatters: Array.from(awayBS.values()),
    homePitchers: homePS, awayPitchers: awayPS,
    homeFielding: Array.from(homeFL.values()), awayFielding: Array.from(awayFL.values()),
    winningPitcherId: winId, losingPitcherId: lossId, savePitcherId: null,
    atBats,
  };
}
